import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createCodeNode, $isCodeNode } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  $isListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
} from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";
import { useEffect, useState, type ReactNode } from "react";
import {
  BoldIcon,
  BracesIcon,
  CheckSquareIcon,
  CodeIcon,
  Heading2Icon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
  RemoveFormattingIcon,
  StrikethroughIcon,
  TypeIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { isSafeMarkdownLink } from "./markdownSupport";

interface ToolbarButtonProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly active?: boolean;
  readonly size?: "default" | "compact";
  readonly disabled?: boolean;
}

function ToolbarButton({
  label,
  children,
  onClick,
  active = false,
  size = "default",
  disabled = false,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none",
        RESPONSIVE_ICON_BUTTON_CLASS,
        size === "compact"
          ? "md:size-7 focus-visible:ring-1 focus-visible:ring-ring"
          : "md:size-8 focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? size === "compact"
            ? "bg-muted text-foreground"
            : "bg-muted/70 text-foreground"
          : null,
      )}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="mx-0.5 h-5 w-px shrink-0 bg-border/70" aria-hidden />;
}

interface ToolbarSelectionState {
  readonly block: "paragraph" | "heading" | "quote" | "code";
  readonly bold: boolean;
  readonly italic: boolean;
  readonly strikethrough: boolean;
  readonly inlineCode: boolean;
  readonly link: boolean;
  readonly list: "bullet" | "number" | "check" | null;
}

const EMPTY_SELECTION_STATE: ToolbarSelectionState = {
  block: "paragraph",
  bold: false,
  italic: false,
  strikethrough: false,
  inlineCode: false,
  link: false,
  list: null,
};

function readSelectionState(editor: LexicalEditor): ToolbarSelectionState {
  return editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return EMPTY_SELECTION_STATE;
    let node: LexicalNode | null = selection.anchor.getNode();
    let link = false;
    let list: ToolbarSelectionState["list"] = null;
    while (node) {
      if ($isLinkNode(node)) link = true;
      if ($isListNode(node)) list = node.getListType();
      node = node.getParent();
    }
    const topLevel = selection.anchor.getNode().getTopLevelElement();
    const block = $isHeadingNode(topLevel)
      ? "heading"
      : $isQuoteNode(topLevel)
        ? "quote"
        : $isCodeNode(topLevel)
          ? "code"
          : "paragraph";
    return {
      block,
      bold: selection.hasFormat("bold"),
      italic: selection.hasFormat("italic"),
      strikethrough: selection.hasFormat("strikethrough"),
      inlineCode: selection.hasFormat("code"),
      link,
      list,
    };
  });
}

export interface MarkdownEditorToolbarProps {
  /** compact keeps only the writing essentials for status-update composers. */
  readonly variant?: "default" | "compact";
  readonly disabled?: boolean;
}

export function MarkdownEditorToolbar({
  variant = "default",
  disabled = false,
}: MarkdownEditorToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const [selectionState, setSelectionState] = useState(() => readSelectionState(editor));
  const compact = variant === "compact";
  const buttonSize = compact ? "compact" : "default";
  const iconClass = compact ? "size-3.5" : "size-4";
  useEffect(
    () =>
      editor.registerUpdateListener(() => {
        setSelectionState(readSelectionState(editor));
      }),
    [editor],
  );
  const setBlock = (kind: "paragraph" | "heading" | "quote" | "code") => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (kind === "heading") {
        $setBlocksType(selection, () => $createHeadingNode("h2"));
        return;
      }
      if (kind === "quote") {
        $setBlocksType(selection, $createQuoteNode);
        return;
      }
      if (kind === "code") {
        $setBlocksType(selection, () => $createCodeNode());
        return;
      }
      $setBlocksType(selection, $createParagraphNode);
    });
  };
  const editLink = () => {
    const href = window.prompt("Link URL (leave blank to remove the link)");
    if (href === null) return;
    const value = href.trim();
    if (value && !isSafeMarkdownLink(value)) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, value || null);
  };

  return (
    <fieldset
      disabled={disabled}
      className={cn(
        "flex items-center gap-[8px] overflow-x-auto md:gap-0.5",
        compact
          ? "min-h-8 border-b border-border/50 px-1 py-0.5"
          : "min-h-10 border-b border-border/60 bg-muted/15 px-1.5 py-1",
      )}
      role="toolbar"
      aria-label="Markdown formatting"
      aria-disabled={disabled}
    >
      {compact ? null : (
        <>
          <ToolbarButton
            label="Paragraph"
            size={buttonSize}
            active={selectionState.block === "paragraph" && selectionState.list === null}
            onClick={() => setBlock("paragraph")}
          >
            <TypeIcon className={iconClass} />
          </ToolbarButton>
          <ToolbarButton
            label="Heading"
            size={buttonSize}
            active={selectionState.block === "heading"}
            onClick={() => setBlock("heading")}
          >
            <Heading2Icon className={iconClass} />
          </ToolbarButton>
          <ToolbarButton
            label="Quote"
            size={buttonSize}
            active={selectionState.block === "quote"}
            onClick={() => setBlock("quote")}
          >
            <QuoteIcon className={iconClass} />
          </ToolbarButton>
          <ToolbarButton
            label="Code block"
            size={buttonSize}
            active={selectionState.block === "code"}
            onClick={() => setBlock("code")}
          >
            <BracesIcon className={iconClass} />
          </ToolbarButton>
          <ToolbarDivider />
        </>
      )}
      <ToolbarButton
        label="Bold"
        size={buttonSize}
        active={selectionState.bold}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
      >
        <BoldIcon className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        size={buttonSize}
        active={selectionState.italic}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
      >
        <ItalicIcon className={iconClass} />
      </ToolbarButton>
      {compact ? null : (
        <>
          <ToolbarButton
            label="Strikethrough"
            size={buttonSize}
            active={selectionState.strikethrough}
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")}
          >
            <StrikethroughIcon className={iconClass} />
          </ToolbarButton>
          <ToolbarButton
            label="Inline code"
            size={buttonSize}
            active={selectionState.inlineCode}
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")}
          >
            <CodeIcon className={iconClass} />
          </ToolbarButton>
        </>
      )}
      <ToolbarButton label="Link" size={buttonSize} active={selectionState.link} onClick={editLink}>
        <LinkIcon className={iconClass} />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton
        label="Bulleted list"
        size={buttonSize}
        active={selectionState.list === "bullet"}
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
      >
        <ListIcon className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        size={buttonSize}
        active={selectionState.list === "number"}
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
      >
        <ListOrderedIcon className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        label="Checklist"
        size={buttonSize}
        active={selectionState.list === "check"}
        onClick={() => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)}
      >
        <CheckSquareIcon className={iconClass} />
      </ToolbarButton>
      {compact ? null : (
        <ToolbarButton
          label="Remove list"
          size={buttonSize}
          onClick={() => editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)}
        >
          <RemoveFormattingIcon className={iconClass} />
        </ToolbarButton>
      )}
    </fieldset>
  );
}
